import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import { useEditor, EditorContent, EditorContext } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { ImageWithAlign } from './extensions/ImageAlign';
import { Audio } from './extensions/Audio';
import { LinkBlock } from './extensions/LinkBlock';
import { tiptapToContentBlocks, contentBlocksToTiptap } from './serialization';
import EditorToolbar from './EditorToolbar';
import LinkBubbleMenu from './LinkBubbleMenu';

const getExtensions = (getPlaceholder) => [
  StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
  Placeholder.configure({
    placeholder: getPlaceholder,
    showOnlyCurrent: false,
  }),
  Underline,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Link.configure({
    openOnClick: false,
    enableClickSelection: true,
    HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
  }),
  ImageWithAlign.configure({
    HTMLAttributes: { class: 'rounded-xl shadow-md max-w-full h-auto' },
    resize: {
      enabled: true,
      directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      minWidth: 50,
      minHeight: 50,
      alwaysPreserveAspectRatio: true,
    },
  }),
  Audio,
  LinkBlock,
];

/**
 * RichTextEditor - Google Docs-like editor for article/discussion content.
 * Exposes insertImage(url) and insertAudio(url) via ref for parent to call after upload.
 */
const RichTextEditor = forwardRef(function RichTextEditor(
  { contentBlocks = [], onChange, onRequestInsertImage, onRequestInsertAudio, titleSlot, placeholder = 'احكِ قصتك...' },
  ref
) {
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;
  const toolbarRef = useRef(null);

  const getPlaceholder = useCallback(() => placeholderRef.current || 'احكِ قصتك...', []);

  const initialContent = contentBlocksToTiptap(contentBlocks);

  const handleUpdate = useCallback(
    ({ editor: ed }) => {
      const doc = ed.getJSON();
      const blocks = tiptapToContentBlocks(doc);
      onChange?.(blocks);
    },
    [onChange]
  );

  const editor = useEditor({
    extensions: getExtensions(getPlaceholder),
    content: initialContent,
    onUpdate: handleUpdate,
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[60vh]',
        style: 'font-family: Amiri, serif; font-size: 21px; line-height: 1.8; color: #2C2B29; direction: rtl;',
        dir: 'rtl',
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length && onRequestInsertImage) {
          const file = files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            onRequestInsertImage(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const files = event.clipboardData?.files;
        if (files?.length && onRequestInsertImage) {
          const file = files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            onRequestInsertImage(file);
            return true;
          }
        }
        return false;
      },
      handleDOMEvents: {
        click: (view, event) => {
          const link = event.target?.closest?.('a');
          if (link && view.editable) {
            if (event.metaKey || event.ctrlKey) {
              const href = link.getAttribute('href');
              if (href) window.open(href, '_blank', 'noopener,noreferrer');
              return true;
            }
            return false;
          }
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.view.dispatch(editor.state.tr);
    }
  }, [placeholder, editor]);

  useEffect(() => {
    const el = editor?.view?.dom;
    if (!el) return;
    const onCapture = (e) => {
      const link = e.target?.closest?.('a');
      if (link) {
        e.preventDefault();
      }
    };
    el.addEventListener('click', onCapture, true);
    return () => el.removeEventListener('click', onCapture, true);
  }, [editor]);

  useImperativeHandle(ref, () => ({
    insertImage: (url) => editor?.commands.setImage({ src: url }),
    insertAudio: (url) => editor?.chain().focus().insertContent({ type: 'audio', attrs: { src: url } }).run(),
  }), [editor]);

  if (!editor) return null;

  const contextValue = useMemo(() => ({ editor }), [editor]);

  return (
    <EditorContext.Provider value={contextValue}>
      <div className="space-y-4">
        <EditorToolbar
          ref={toolbarRef}
          editor={editor}
          onInsertImage={onRequestInsertImage}
          onInsertAudio={onRequestInsertAudio}
        />
        {titleSlot}
        <EditorContent editor={editor} />
        <LinkBubbleMenu editor={editor} onOpenEdit={() => toolbarRef.current?.openLinkEdit?.()} />
      </div>
    </EditorContext.Provider>
  );
});

export default RichTextEditor;
export { tiptapToContentBlocks, contentBlocksToTiptap };
